<template>
  <div>
    <div class="user-container">
      <div class="w-50" v-if="allows_crud">
        <NewGenericBTable
          ref="available_table"
          :display_id="'NewGenericBTable' + key_component"
          :key="generic_table_key"
          :search_filter="true"
          :columns_display="true"
          :fields="fields"
          :selection_mode="allows_crud"
          :select_mode="'range'"
          :pre_filter="pre_filter"
          :pre_multiple_search="pre_multiple_search"
          @selected="slotSelectedLeft"
          :url="available_users_url"
        >
          <template #cell(first_name)="row">
            {{ row.item.first_name }} {{ row.item.last_name }}
          </template>
        </NewGenericBTable>
      </div>
      <div class="align-self-center">
        <div class="buttons-container">
          <b-button
            v-if="allows_crud"
            variant="info"
            @click="moveToSelected"
            size="sm"
            class="mb-2"
          >
            <b-icon-arrow-right></b-icon-arrow-right>
          </b-button>
          <b-button
            v-if="
              allows_crud && (!closed_evaluatee || (user && user.is_superuser))
            "
            variant="info"
            @click="moveToUnSelected"
            size="sm"
          >
            <b-icon-arrow-left></b-icon-arrow-left>
          </b-button>
        </div>
      </div>
      <div :class="{ 'w-100': !allows_crud }" class="w-50">
        <GenericBTable
          :display_id="'GenericBTable_' + key_component"
          :items="users_container_right"
          :fields="fields2"
          :columns_display="true"
          :selection_mode="
            allows_crud && (!closed_evaluatee || (user && user.is_superuser))
          "
          :show_select_mode="
            allows_crud && (!closed_evaluatee || (user && user.is_superuser))
          "
          :show_pagination="true"
          :pagination="10"
          :select_mode="'range'"
          @selected="slotSelectedRight"
        >
          <template #cell(first_name)="row">
            {{ row.item.first_name }} {{ row.item.last_name }}
          </template>
        </GenericBTable>
      </div>
    </div>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { groupUserUrl } from "@/utils/globals";
import { mapGetters } from "vuex";
export default {
  name: "NewUserList",
  components: {
    NewGenericBTable: () => import("@/components/reusable/NewGenericBTable"),
    GenericBTable: () => import("@/components/reusable/GenericBTable"),
  },
  props: {
    key_component: {
      type: String,
    },
    users_ids: {
      type: Array,
    },
    // Usuarios ya asignados, resueltos por el padre con ?expand=. Cuando viene,
    // la tabla de la derecha se arma con esto y no se pide nada al servidor.
    users_detail: {
      type: Array,
      default: null,
    },
    pre_multiple_search: {
      type: Array,
      default() {
        return [];
      },
    },
    allows_crud: {
      type: Boolean,
      default: true,
    },
    // Nombre del grupo al que se restringe la lista de disponibles ('ESTUDIANTES',
    // 'PROFESORES'). Decide el endpoint: cada grupo con listado propio lo impone
    // por ruta, en vez de mandarlo como filtro que el cliente puede quitar.
    required_group_name: {
      type: String,
      default: "",
    },
    // Filtros adicionales del queryset. No usar pre_multiple_search para filtrar:
    // es texto libre y arrastra a cualquiera cuyo correo o nombre contenga la palabra.
    pre_filter_array: {
      type: Array,
      default() {
        return [];
      },
    },
    closed_evaluatee: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      generic_table_key: 0,
      assigned_loaded: false,
      users_container_right: [],
      left_selected_user: [],
      right_selected_user: [],
    };
  },
  computed: {
    ...mapGetters({
      user: "getUser",
      roles: names.ROLES,
    }),
    group_user_url() {
      return groupUserUrl(this.required_group_name);
    },
    available_users_url() {
      return this.group_user_url || "/authentication/user/";
    },
    pre_filter() {
      // Con endpoint propio del grupo, el alcance y la forma los impone la ruta.
      // Sin él se cae al listado general y hay que pedir ambas cosas por query.
      let partial_url = this.group_user_url
        ? []
        : ["picker=True"].concat(
            this.required_group_name
              ? [`groups__name=${this.required_group_name}`]
              : []
          );
      if (this.users_container_right.length > 0)
        partial_url.push(
          "id__exclude__in=" +
            this.users_container_right.map((x) => x.id).join(",")
        );
      if (this.pre_filter_array.length > 0)
        partial_url = partial_url.concat(this.pre_filter_array);
      return partial_url.join("&");
    },
    fields() {
      return [
        {
          key: "first_name",
          label: this.$t("reusable.common.user"),
          sortable: true,
          display_column: true,
          thStyle: { background: "var(--primary-color) !important" },
        },
        {
          key: "user_data",
          label: this.$t("reusable.common.identifier"),
          sortable: true,
          // La celda muestra el identificador que cuelga de user_data
          ordering_key: "user_data__identifier",
          display_column: false,
          thStyle: { background: "var(--primary-color) !important" },
          formatter: (value) => {
            return value ? value.identifier : "";
          },
        },
        {
          key: "email",
          label: this.$t("reusable.common.email2"),
          sortable: true,
          display_column: false,
          thStyle: { background: "var(--primary-color) !important" },
        },
        {
          key: "role",
          label: this.$t("reusable.common.role"),
          sortable: true,
          // La celda muestra el nombre del cargo, no su id
          ordering_key: "access__role__position_name",
          display_column: true,
          thStyle: { background: "var(--primary-color) !important" },
          formatter: (value) => {
            const rol = this.roles.find((x) => x.id == value);
            return rol ? rol.position_name : "";
          },
        },
      ];
    },
    fields2() {
      return [
        {
          key: "first_name",
          label: this.$t("reusable.common.user"),
          sortable: true,
          display_column: true,
          thStyle: { background: "var(--primary-color) !important" },
        },
        {
          key: "user_data",
          label: this.$t("reusable.common.identifier"),
          sortable: true,
          // La celda muestra el identificador que cuelga de user_data
          sortByFormatted: true,
          display_column: false,
          thStyle: { background: "var(--primary-color) !important" },
          formatter: (value) => {
            return value ? value.identifier : "";
          },
        },
        {
          key: "email",
          label: this.$t("reusable.common.email2"),
          sortable: true,
          display_column: false,
          thStyle: { background: "var(--primary-color) !important" },
        },
        {
          key: "role",
          label: this.$t("reusable.common.role"),
          sortable: true,
          // La celda muestra el nombre del cargo, no su id
          sortByFormatted: true,
          display_column: true,
          thStyle: { background: "var(--primary-color) !important" },
          formatter: (value) => {
            const rol = this.roles.find((x) => x.id == value);
            return rol ? rol.position_name : "";
          },
        },
      ];
    },
  },
  methods: {
    slotSelectedLeft(rows) {
      this.left_selected_user = rows;
    },
    slotSelectedRight(rows) {
      this.right_selected_user = rows;
    },
    refreshAvailable() {
      // Refresca la tabla de disponibles sin remontarla: conserva página, búsqueda
      // y columnas, y evita el parpadeo del remonte por :key
      this.$nextTick(() => {
        const table = this.$refs.available_table;
        if (table) table.fetchData(1);
        else this.generic_table_key++;
      });
    },
    loadAssignedUsers() {
      if (this.users_detail != null) {
        this.assigned_loaded = true;
        if (!this.users_ids) {
          this.users_container_right = this.users_detail.slice();
          this.refreshAvailable();
          return;
        }
        const by_id = new Map(this.users_detail.map((x) => [x.id, x]));
        const apply = () => {
          this.users_container_right = this.users_ids
            .map((id) => by_id.get(id))
            .filter(Boolean);
          this.refreshAvailable();
        };
        apply();
        // Selección sin guardar: no viene en lo que resolvió el padre
        const missing = this.users_ids.filter((id) => !by_id.has(id));
        if (missing.length == 0) return;
        this.fetchUsersById(missing).then((users) => {
          users.forEach((user) => by_id.set(user.id, user));
          apply();
        });
        return;
      }
      if (!this.users_ids || this.users_ids.length == 0) return;
      this.fetchUsersById(this.users_ids).then((users) => {
        this.users_container_right = users;
        this.assigned_loaded = true;
        this.refreshAvailable();
      });
    },
    fetchUsersById(ids) {
      return this.$restful
        .GetAll(
          `/authentication/user/?picker=True&id__in=${ids.join(
            ","
          )}`
        )
        .then((results) => results);
    },
    moveToSelected() {
      const left_selected_user = this.left_selected_user;
      left_selected_user.forEach((user, index) => {
        if (!this.users_container_right.find((x) => x.id == user.id))
          this.users_container_right.push(user);
        if (left_selected_user.length == index + 1) {
          this.left_selected_user = [];
          this.refreshAvailable();
          this.$emit("selected_user", this.users_container_right);
        }
      });
    },
    moveToUnSelected() {
      const right_selected_user = this.right_selected_user;
      right_selected_user.forEach((user, index) => {
        const index_user = this.users_container_right.findIndex(
          (x) => x.id == user.id
        );
        if (index_user != -1) this.users_container_right.splice(index_user, 1);
        if (right_selected_user.length == index + 1) {
          this.right_selected_user = [];
          this.refreshAvailable();
          this.$emit("selected_user", this.users_container_right);
        }
      });
    },
  },
  watch: {
    // El padre puede resolver los usuarios después de montar el componente
    users_detail() {
      if (!this.assigned_loaded) this.loadAssignedUsers();
    },
  },
  created() {
    this.loadAssignedUsers();
  },
};
</script>
<style scoped>
.buttons-container {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
}
.user-container {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
}
</style>

